/**
 * GlobalFilters Component
 *
 * Persistent filter bar for analytics dashboards
 * Filters apply across all dashboard tabs
 */

import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Button,
  SelectChangeEvent,
} from '@mui/material';
import { PredictProgram } from '~/types/predict';
import { AnalyticsFilters } from '~/types/analytics';
import { getDateRangePreset } from '~/utils/generateMockAnalytics';
import { usePredictContext } from '~/hooks/predict/usePredictContext';

interface GlobalFiltersProps {
  filters: AnalyticsFilters;
  onFiltersChange: (filters: AnalyticsFilters) => void;
  facilities: { id: string; name: string }[];
}

export const LOCATION_TYPES = [
  { value: 'ED', label: 'ED' },
  { value: 'INPATIENT', label: 'Inpatient' },
];

export const PROGRAMS = [
  { value: PredictProgram.HOME_HEALTH, label: 'Home Health' },
  // { value: PredictProgram.HOSPITAL_AT_HOME, label: 'Hospital at Home' }, // Hidden - not yet available
  { value: PredictProgram.SNF_AT_HOME, label: 'SNF at Home' },
  { value: PredictProgram.HOSPICE, label: 'Hospice' },
  { value: PredictProgram.INFUSION_AT_HOME, label: 'Infusion at Home' },
  { value: PredictProgram.PALLIATIVE, label: 'Palliative' },
];

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
];

const OWNER_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'ME', label: 'My Patients' },
  { value: 'UNASSIGNED', label: 'Unassigned' },
];

const PROGRAM_LABELS: Record<PredictProgram, string> = {
  [PredictProgram.HOME_HEALTH]: 'Home Health',
  [PredictProgram.HOSPITAL_AT_HOME]: 'Hospital at Home',
  [PredictProgram.SNF_AT_HOME]: 'SNF at Home',
  [PredictProgram.HOSPICE]: 'Hospice',
  [PredictProgram.INFUSION_AT_HOME]: 'Infusion at Home',
  [PredictProgram.PALLIATIVE]: 'Palliative',
};

const GlobalFilters: React.FC<GlobalFiltersProps> = ({ filters, onFiltersChange, facilities }) => {
  const [datePreset, setDatePreset] = useState('30d');
  const { visiblePrograms } = usePredictContext();

  const activePrograms = visiblePrograms.map(p => ({ value: p, label: PROGRAM_LABELS[p] }));

  const handleFacilityChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;

    // Check if "Select All" was clicked
    if (selectedValues.includes('__ALL__')) {
      const allFacilityIds = facilities.map(f => f.id);
      onFiltersChange({
        ...filters,
        facilities: allFacilityIds,
      });
      return;
    }

    // If all were previously selected and user clicks a specific option, select only that option
    const allFacilityIds = facilities.map(f => f.id);
    const allWereSelected = filters.facilities.length === allFacilityIds.length;

    if (allWereSelected && selectedValues.length === allFacilityIds.length - 1) {
      // User deselected one item when all were selected
      // Find which one was deselected (the one NOT in selectedValues)
      const deselectedItem = allFacilityIds.find(id => !selectedValues.includes(id));
      if (deselectedItem) {
        // Select ONLY the deselected item (counterintuitive but matches user intent)
        onFiltersChange({
          ...filters,
          facilities: [deselectedItem],
        });
        return;
      }
    }

    onFiltersChange({
      ...filters,
      facilities: selectedValues,
    });
  };

  const handleLocationTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;

    // Check if "Select All" was clicked
    if (selectedValues.includes('__ALL__')) {
      const allLocationTypes = LOCATION_TYPES.map(t => t.value);
      onFiltersChange({
        ...filters,
        locationTypes: allLocationTypes,
      });
      return;
    }

    // If all were previously selected and user clicks a specific option, select only that option
    const allLocationTypes = LOCATION_TYPES.map(t => t.value);
    const allWereSelected = filters.locationTypes.length === allLocationTypes.length;

    if (allWereSelected && selectedValues.length === allLocationTypes.length - 1) {
      const deselectedItem = allLocationTypes.find(type => !selectedValues.includes(type));
      if (deselectedItem) {
        onFiltersChange({
          ...filters,
          locationTypes: [deselectedItem],
        });
        return;
      }
    }

    onFiltersChange({
      ...filters,
      locationTypes: selectedValues,
    });
  };

  const handleProgramChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    const selectedValues = value;

    // Check if "Select All" was clicked
    if (selectedValues.includes('__ALL__')) {
      const allPrograms = activePrograms.map(p => p.value);
      onFiltersChange({
        ...filters,
        programs: allPrograms,
      });
      return;
    }

    // If all were previously selected and user clicks a specific option, select only that option
    const allPrograms = activePrograms.map(p => p.value);
    const allWereSelected = filters.programs.length === allPrograms.length;

    if (allWereSelected && selectedValues.length === allPrograms.length - 1) {
      const deselectedItem = allPrograms.find(prog => !selectedValues.includes(prog));
      if (deselectedItem) {
        onFiltersChange({
          ...filters,
          programs: [deselectedItem as PredictProgram],
        });
        return;
      }
    }

    onFiltersChange({
      ...filters,
      programs: selectedValues as PredictProgram[],
    });
  };

  const handleDateRangeChange = (event: SelectChangeEvent<string>) => {
    const preset = event.target.value as 'today' | '7d' | '30d' | '90d';
    setDatePreset(preset);
    const dateRange = getDateRangePreset(preset);
    onFiltersChange({
      ...filters,
      dateRange,
    });
  };

  const handleOwnerChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      ownerFilter: event.target.value as 'ALL' | 'ME' | 'UNASSIGNED',
    });
  };

  const handleReset = () => {
    setDatePreset('30d');
    onFiltersChange({
      facilities: facilities.map(f => f.id), // Reset to all accessible facilities
      locationTypes: LOCATION_TYPES.map(lt => lt.value), // Reset to all location types
      programs: activePrograms.map(p => p.value), // Reset to all programs
      dateRange: getDateRangePreset('30d'),
      ownerFilter: 'ALL',
    });
  };

  // Count active filters (filters that are actively narrowing results)
  // Don't count facilities if all are selected (default state) or if there's only 1
  const allFacilitiesSelected = filters.facilities.length === facilities.length;
  const facilityFilterActive = facilities.length > 1 && !allFacilitiesSelected && filters.facilities.length > 0;

  // Don't count location types and programs if all are selected (default state)
  const allLocationTypesSelected = filters.locationTypes.length === LOCATION_TYPES.length;
  const allProgramsSelected = filters.programs.length === activePrograms.length;

  const activeFilterCount =
    (facilityFilterActive ? 1 : 0) +
    (!allLocationTypesSelected && filters.locationTypes.length > 0 ? 1 : 0) +
    (!allProgramsSelected && filters.programs.length > 0 ? 1 : 0) +
    (filters.ownerFilter !== 'ALL' ? 1 : 0);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        mb: 3,
      }}
    >

      {/* Facility Filter - Only show if multiple facilities available */}
      {facilities.length > 1 && (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Facility</InputLabel>
          <Select
            multiple
            value={filters.facilities}
            onChange={handleFacilityChange}
            input={<OutlinedInput label="Facility" />}
            renderValue={(selected) =>
              selected.length === 0 ? (
                'All'
              ) : selected.length === facilities.length ? (
                'All'
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const facility = facilities.find((f) => f.id === value);
                    return <Chip key={value} label={facility?.name || value} size="small" />;
                  })}
                </Box>
              )
            }
          >
            <MenuItem
              value="__ALL__"
              disabled={filters.facilities.length === facilities.length}
              sx={{ fontWeight: 600 }}
            >
              Select All
            </MenuItem>
            {facilities.map((facility) => (
              <MenuItem key={facility.id} value={facility.id}>
                {facility.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Location Type Filter */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Location Type</InputLabel>
        <Select
          multiple
          value={filters.locationTypes}
          onChange={handleLocationTypeChange}
          input={<OutlinedInput label="Location Type" />}
          renderValue={(selected) =>
            selected.length === 0 ? (
              'All'
            ) : selected.length === LOCATION_TYPES.length ? (
              'All'
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )
          }
        >
          <MenuItem
            value="__ALL__"
            disabled={filters.locationTypes.length === LOCATION_TYPES.length}
            sx={{ fontWeight: 600 }}
          >
            Select All
          </MenuItem>
          {LOCATION_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Program Filter */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Program</InputLabel>
        <Select
          multiple
          value={filters.programs}
          onChange={handleProgramChange}
          input={<OutlinedInput label="Program" />}
          renderValue={(selected) =>
            selected.length === 0 ? (
              'All'
            ) : selected.length === activePrograms.length ? (
              'All'
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const program = activePrograms.find((p) => p.value === value);
                  return <Chip key={value} label={program?.label || value} size="small" />;
                })}
              </Box>
            )
          }
        >
          <MenuItem
            value="__ALL__"
            disabled={filters.programs.length === activePrograms.length}
            sx={{ fontWeight: 600 }}
          >
            Select All
          </MenuItem>
          {activePrograms.map((program) => (
            <MenuItem key={program.value} value={program.value}>
              {program.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Date Range Filter */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Date Range</InputLabel>
        <Select value={datePreset} onChange={handleDateRangeChange} label="Date Range">
          {DATE_PRESETS.map((preset) => (
            <MenuItem key={preset.value} value={preset.value}>
              {preset.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Owner Filter */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Owner</InputLabel>
        <Select value={filters.ownerFilter} onChange={handleOwnerChange} label="Owner">
          {OWNER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Reset */}
      {activeFilterCount > 0 && (
        <Button onClick={handleReset}>
          Reset All
        </Button>
      )}
    </Box>
  );
};

export default GlobalFilters;
